/**
 * ScoreSparkline — tiny inline SVG showing score trend over time.
 * Expects `points: Array<{ score: number, created_at?: string }>`.
 * Width/height auto-scale; stroke is teal unless the last point is REJECTED.
 */
import { formatOverallScore } from '../../shared/scoreDisplay.js';

const W = 120;
const H = 32;
const PAD = 2;

export function ScoreSparkline({ points = [], width = W, height = H, rejected = false }) {
  if (!points.length) {
    return (
      <svg
        className="score-sparkline is-empty"
        width={width}
        height={height}
        viewBox={`0 0 ${width} ${height}`}
        aria-label="No trend data"
        role="img"
      >
        <line
          x1={PAD}
          y1={height / 2}
          x2={width - PAD}
          y2={height / 2}
          stroke="#D5DBE2"
          strokeWidth="1"
          strokeDasharray="3 3"
        />
      </svg>
    );
  }

  const scores = points.map((p) => (Number.isFinite(p.score) ? p.score : 0));
  const min = Math.min(...scores, 0);
  const max = Math.max(...scores, 100);
  const range = max - min || 1;

  const step = (width - PAD * 2) / Math.max(points.length - 1, 1);
  const coords = scores.map((s, i) => {
    const x = PAD + i * step;
    const y = height - PAD - ((s - min) / range) * (height - PAD * 2);
    return [x, y];
  });
  const path = coords
    .map(([x, y], i) => (i === 0 ? `M ${x} ${y}` : `L ${x} ${y}`))
    .join(' ');

  const stroke = rejected ? '#C94A4A' : '#1A9E8F';
  const lastX = coords[coords.length - 1][0];
  const lastY = coords[coords.length - 1][1];

  return (
    <svg
      className="score-sparkline"
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      role="img"
      aria-label={`Score trend: ${scores.map((s) => formatOverallScore(s)).join(', ')}`}
    >
      <path d={path} fill="none" stroke={stroke} strokeWidth="1.6" strokeLinejoin="round" strokeLinecap="round" />
      <circle cx={lastX} cy={lastY} r="2.2" fill={stroke} />
    </svg>
  );
}
